/**
 * Image Generator State Management Hook
 *
 * Consolidates all image generator state into a single useReducer,
 * with preset CRUD, enhancement history, and localStorage persistence.
 */

import { useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { ModelDefinitionListItem, ImageGeneratorPreferences, api } from '@/lib/api';
import {
  ImageInstanceConfig,
  ImageInstanceState,
  ImageModelSettings,
  ImagePanelFields,
  ImagePreset,
  GeneratedImage,
  EnhancementKey,
  ModelSizeCapability,
  TagDefinition,
  ReferenceImage,
  NUM_INSTANCES,
  MAX_HISTORY_ITEMS,
  EMPTY_ENHANCEMENTS,
  ARCHE_ID,
  configMatchesPreset,
  createDefaultInstanceState,
  createDefaultModelSettings,
  getActiveModelSettings,
  isUnsavedPresetName,
  toBasePresetName,
  toUnsavedPresetName,
  repairSizeForModel,
  resolveModelSettings,
} from '../types/image-generator.types';
import { ImageMode, BUILT_IN_MODES, resolveBuiltInModes } from '../types/image-generator-modes';
import {
  loadBuiltInModeTags,
  loadEnhancementHistory,
  loadInstanceConfigs,
  loadInstanceImages,
  loadPanelLayout,
  loadPresets,
  loadTagDefinitions,
  loadUserModes,
  saveBuiltInModeTags,
  saveEnhancementHistory,
  saveInstanceConfigs,
  saveInstanceImages,
  savePanelLayout,
  savePresets,
  saveTagDefinitions,
  saveUserModes,
} from '../lib/image-generator-storage';

// ============================================================================
// State Shape
// ============================================================================

export interface ImageGeneratorState {
  instanceConfigs: ImageInstanceConfig[];
  instanceStates: ImageInstanceState[];
  enabledInstances: boolean[];
  minimizedInstances: boolean[];
  instanceImages: GeneratedImage[][];
  enhancementHistory: Record<EnhancementKey, string[]>;
  presets: ImagePreset[];
  tagDefinitions: TagDefinition[];
  modes: ImageMode[];
}

// ============================================================================
// Action Types
// ============================================================================

/**
 * Panel-level updates only. Size / quality / style / engine params are NOT here:
 * they live per model and go through `SET_MODEL` / `UPDATE_MODEL_SETTINGS`, and
 * keeping them out of this type is what makes writing them down the wrong path a
 * compile error rather than a silent write into the wrong model's entry.
 */
export type PanelConfigUpdates = Partial<ImagePanelFields>;

/** Tail options for `setInstanceModel`. */
export interface SetInstanceModelOptions {
  /** Omit to keep the panel's current provider. */
  provider?: string;
  /** Catalogue used to repair the destination size; absent before the fetch lands. */
  models?: ModelSizeCapability[];
  /**
   * REPLACES the destination model's entry instead of seeding it — the preset /
   * mode / assistant "here is the recipe" path. Passing it here rather than as a
   * follow-up `updateModelSettings` is what makes a combined model+settings
   * change one atomic reducer pass, with no order to get wrong.
   */
  settings?: Partial<ImageModelSettings>;
}

type ImageGeneratorAction =
  | { type: 'UPDATE_CONFIG'; instanceId: number; updates: PanelConfigUpdates }
  | {
      /**
       * Switches a panel's model, resolving the destination entry: reuse what the
       * panel already remembers for that model, else seed a copy of the most
       * recent settings for it on any panel, else defaults.
       *
       * `settings` and `panel` ride along on purpose. Every combined update
       * (preset-with-model, mode apply, the assistant's `configure_all_panels`)
       * changes the model AND the settings together; splitting that across two
       * actions would create a correct order and a silently wrong one, where the
       * settings land in the OUTGOING model's entry and persist there with no
       * error. One action removes the hazard structurally.
       */
      type: 'SET_MODEL';
      instanceId: number;
      modelName: string;
      /** Omitted when the caller has no better answer (a legacy preset that stored a model but no provider) — the panel keeps its current provider. */
      provider?: string;
      at: number;
      /** Catalogue used to repair the destination size; absent before the fetch lands. */
      models?: ModelSizeCapability[];
      /** When present, REPLACES the resolved entry instead of seeding it. */
      settings?: Partial<ImageModelSettings>;
      /** Panel-level fields applied in the same pass. See `applyPanelUpdates`. */
      panel?: PanelConfigUpdates;
    }
  | {
      /** Writes the ACTIVE model's entry. Every other entry is left untouched. */
      type: 'UPDATE_MODEL_SETTINGS';
      instanceId: number;
      updates: Partial<ImageModelSettings>;
      at: number;
      models?: ModelSizeCapability[];
      panel?: PanelConfigUpdates;
    }
  | { type: 'SET_CONFIGS'; configs: ImageInstanceConfig[] }
  | { type: 'UPDATE_STATE'; instanceId: number; updates: Partial<ImageInstanceState> }
  | { type: 'TOGGLE_INSTANCE'; instanceId: number }
  | { type: 'TOGGLE_MINIMIZED'; instanceId: number }
  | { type: 'ADD_IMAGE'; instanceId: number; image: GeneratedImage }
  | { type: 'REMOVE_IMAGE'; instanceId: number; requestId: string }
  | { type: 'CLEAR_ALL_IMAGES' }
  | { type: 'SET_IMAGES'; instanceId: number; images: GeneratedImage[] }
  | { type: 'ADD_PRESET'; preset: ImagePreset }
  | { type: 'DELETE_PRESET'; presetId: string }
  | { type: 'SET_ENHANCEMENT_HISTORY'; key: EnhancementKey; history: string[] }
  | { type: 'ADD_ENHANCEMENT_HISTORY'; key: EnhancementKey; value: string }
  | { type: 'UPDATE_ENHANCEMENT'; instanceId: number; key: EnhancementKey; value: string }
  | { type: 'CLEAR_ENHANCEMENT'; instanceId: number; key: EnhancementKey }
  | {
      type: 'UPDATE_PRESET';
      presetId: string;
      updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>;
    }
  | { type: 'OVERWRITE_PRESET'; presetId: string; instanceId: number }
  | { type: 'ADD_TAG_DEFINITION'; tag: TagDefinition }
  | { type: 'DELETE_TAG_DEFINITION'; tagId: string }
  | { type: 'RENAME_TAG_DEFINITION'; tagId: string; name: string }
  | { type: 'SET_REFERENCE_IMAGE'; instanceId: number; referenceImage: ReferenceImage }
  | { type: 'CLEAR_REFERENCE_IMAGE'; instanceId: number }
  | { type: 'ADD_MODE'; mode: ImageMode }
  | { type: 'DELETE_MODE'; modeId: string }
  | {
      type: 'UPDATE_MODE';
      modeId: string;
      updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>;
    }
  | { type: 'SET_BUILT_IN_MODES'; builtInModes: ImageMode[] };

// ============================================================================
// Reducer
// ============================================================================

/**
 * Panel-level fields whose change means "the settings diverged from the saved
 * preset". Size / quality / style / engine params are not listed because they no
 * longer travel through `UPDATE_CONFIG` at all.
 */
const PANEL_SETTINGS_KEYS: (keyof ImagePanelFields)[] = ['includeModelInPreset', 'enhancements'];

/**
 * Brings `presetName` back in line with what the panel actually holds.
 *
 * Reconciliation is TWO-WAY when the panel was loaded from a real preset: an edit
 * marks it "(Unsaved)", and reverting to the preset's values clears the mark
 * again. Per-model memory makes that matter — a FLUX -> Recraft -> FLUX round trip
 * restores byte-identical settings, and a one-way mark would leave the panel
 * looking permanently dirty after an action that changed nothing.
 *
 * Deliberate carve-out: when no saved preset resolves — the synthetic
 * "Default Preset", or a mode name — there is no recipe to compare against, so
 * the old one-way behaviour stands (mark dirty, never un-mark). A synthetic
 * baseline is not worth inventing: it would have to guess a per-model default
 * size and would fight the size repair.
 */
function reconcilePresetName(
  config: ImageInstanceConfig,
  presets: ImagePreset[],
): ImageInstanceConfig {
  const baseName = toBasePresetName(config.presetName);
  const preset =
    (config.originalPresetId ? presets.find((p) => p.id === config.originalPresetId) : undefined) ??
    presets.find((p) => p.name === baseName);

  if (!preset) {
    if (isUnsavedPresetName(config.presetName)) return config;
    return { ...config, presetName: toUnsavedPresetName(config.presetName) };
  }

  const presetName = configMatchesPreset(config, preset)
    ? preset.name
    : toUnsavedPresetName(preset.name);
  if (presetName === config.presetName && config.originalPresetId === preset.id) return config;
  return { ...config, presetName, originalPresetId: preset.id };
}

/**
 * Applies the panel-level half of a settings action and reconciles the preset
 * name — unless the caller named the preset itself, which is how "apply a preset"
 * and "apply a mode" say *this* is the name, do not second-guess it.
 */
function applyPanelUpdates(
  config: ImageInstanceConfig,
  panel: PanelConfigUpdates | undefined,
  presets: ImagePreset[],
): ImageInstanceConfig {
  const next = panel ? { ...config, ...panel } : config;
  if (panel && 'presetName' in panel) return next;
  return reconcilePresetName(next, presets);
}

/** Replaces the entry for `modelName`, leaving every other entry untouched. */
function withModelSettings(
  config: ImageInstanceConfig,
  modelName: string,
  settings: ImageModelSettings,
): ImageInstanceConfig {
  if (config.modelSettings[modelName] === settings) return config;
  return { ...config, modelSettings: { ...config.modelSettings, [modelName]: settings } };
}

export function imageGeneratorReducer(
  state: ImageGeneratorState,
  action: ImageGeneratorAction,
): ImageGeneratorState {
  switch (action.type) {
    case 'UPDATE_CONFIG': {
      const isSettingsChange = PANEL_SETTINGS_KEYS.some((key) => key in action.updates);
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const next = { ...config, ...action.updates };
        // Bookkeeping-only updates (clearing `originalPresetId` after a preset is
        // deleted, nudging the reference-image strength) must not touch the name.
        if (!isSettingsChange || 'presetName' in action.updates) return next;
        return reconcilePresetName(next, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'SET_MODEL': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;

        const resolved = resolveModelSettings(
          config,
          action.modelName,
          state.instanceConfigs,
          action.models,
        );
        // An explicit `settings` payload REPLACES the resolved entry — the user
        // asked for this recipe on this model, so it becomes what the panel
        // remembers for it. Other models' entries survive untouched.
        const settings = action.settings
          ? repairSizeForModel(
              { ...resolved, ...action.settings, updatedAt: action.at },
              action.modelName,
              action.models,
            )
          : resolved;

        const provider = action.provider || config.provider;
        const unchanged =
          config.modelName === action.modelName &&
          config.provider === provider &&
          config.modelSettings[action.modelName] === settings;
        const withModel = unchanged
          ? config
          : {
              ...withModelSettings(config, action.modelName, settings),
              modelName: action.modelName,
              provider,
            };
        return applyPanelUpdates(withModel, action.panel, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'UPDATE_MODEL_SETTINGS': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const current = getActiveModelSettings(config);
        const settings = repairSizeForModel(
          { ...current, ...action.updates, updatedAt: action.at },
          config.modelName,
          action.models,
        );
        const withSettings = withModelSettings(config, config.modelName, settings);
        return applyPanelUpdates(withSettings, action.panel, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'SET_CONFIGS':
      return { ...state, instanceConfigs: action.configs };

    case 'UPDATE_STATE': {
      const newStates = state.instanceStates.map((s, i) =>
        i === action.instanceId ? { ...s, ...action.updates } : s,
      );
      return { ...state, instanceStates: newStates };
    }

    case 'TOGGLE_INSTANCE': {
      const newEnabled = [...state.enabledInstances];
      const enabledCount = newEnabled.filter(Boolean).length;
      if (newEnabled[action.instanceId] && enabledCount <= 1) {
        return state;
      }
      newEnabled[action.instanceId] = !newEnabled[action.instanceId];
      const newMinimized = [...state.minimizedInstances];
      // Un-minimize when enabling, reset minimized when disabling
      newMinimized[action.instanceId] = false;
      return { ...state, enabledInstances: newEnabled, minimizedInstances: newMinimized };
    }

    case 'TOGGLE_MINIMIZED': {
      const newMinimized = [...state.minimizedInstances];
      if (state.minimizedInstances[action.instanceId]) {
        newMinimized[action.instanceId] = false;
        return { ...state, minimizedInstances: newMinimized };
      }
      const enabledAndExpanded = state.enabledInstances.filter(
        (enabled, i) => enabled && !state.minimizedInstances[i],
      );
      if (enabledAndExpanded.length <= 1) {
        return state;
      }
      newMinimized[action.instanceId] = true;
      return { ...state, minimizedInstances: newMinimized };
    }

    case 'ADD_IMAGE': {
      const updated = [...state.instanceImages];
      if (updated[action.instanceId].some((img) => img.requestId === action.image.requestId)) {
        return state;
      }
      updated[action.instanceId] = [...updated[action.instanceId], action.image];
      return { ...state, instanceImages: updated };
    }

    case 'REMOVE_IMAGE': {
      const updated = [...state.instanceImages];
      updated[action.instanceId] = updated[action.instanceId].filter(
        (img) => img.requestId !== action.requestId,
      );
      return { ...state, instanceImages: updated };
    }

    case 'CLEAR_ALL_IMAGES': {
      return {
        ...state,
        instanceImages: state.instanceImages.map(() => []),
        instanceStates: state.instanceStates.map(() => createDefaultInstanceState()),
        instanceConfigs: state.instanceConfigs.map((config) => ({
          ...config,
          referenceImage: null,
        })),
      };
    }

    case 'SET_IMAGES': {
      const updated = [...state.instanceImages];
      updated[action.instanceId] = action.images;
      return { ...state, instanceImages: updated };
    }

    case 'ADD_PRESET':
      return { ...state, presets: [...state.presets, action.preset] };

    case 'DELETE_PRESET':
      return { ...state, presets: state.presets.filter((p) => p.id !== action.presetId) };

    case 'SET_ENHANCEMENT_HISTORY':
      return {
        ...state,
        enhancementHistory: { ...state.enhancementHistory, [action.key]: action.history },
      };

    case 'ADD_ENHANCEMENT_HISTORY': {
      const existing = state.enhancementHistory[action.key] || [];
      const filtered = existing.filter((item) => item !== action.value);
      return {
        ...state,
        enhancementHistory: {
          ...state.enhancementHistory,
          [action.key]: [action.value, ...filtered].slice(0, MAX_HISTORY_ITEMS),
        },
      };
    }

    case 'UPDATE_ENHANCEMENT': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const updated = {
          ...config,
          enhancements: { ...config.enhancements, [action.key]: action.value },
        };
        return reconcilePresetName(updated, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'CLEAR_ENHANCEMENT': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const updated = {
          ...config,
          enhancements: { ...config.enhancements, [action.key]: '' },
        };
        return reconcilePresetName(updated, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'UPDATE_PRESET': {
      const newPresets = state.presets.map((p) =>
        p.id === action.presetId ? { ...p, ...action.updates } : p,
      );
      return { ...state, presets: newPresets };
    }

    case 'OVERWRITE_PRESET': {
      const config = state.instanceConfigs[action.instanceId];
      if (!config) return state;
      const preset = state.presets.find((p) => p.id === action.presetId);
      if (!preset) return state;

      const settings = getActiveModelSettings(config);
      const presetUpdate: Partial<ImagePreset> = {
        size: settings.size,
        quality: settings.quality,
        style: settings.style,
        enhancements: { ...config.enhancements },
        providerParams: { ...settings.providerParams },
      };
      if (config.includeModelInPreset) {
        presetUpdate.modelName = config.modelName;
        presetUpdate.provider = config.provider;
      } else {
        presetUpdate.modelName = undefined;
        presetUpdate.provider = undefined;
      }

      const newPresets = state.presets.map((p) =>
        p.id === action.presetId ? { ...p, ...presetUpdate } : p,
      );
      const newConfigs = state.instanceConfigs.map((c, i) =>
        i === action.instanceId
          ? { ...c, presetName: preset.name, originalPresetId: preset.id }
          : c,
      );
      return { ...state, presets: newPresets, instanceConfigs: newConfigs };
    }

    case 'ADD_TAG_DEFINITION':
      return { ...state, tagDefinitions: [...state.tagDefinitions, action.tag] };

    case 'DELETE_TAG_DEFINITION': {
      const newTagDefs = state.tagDefinitions.filter((t) => t.id !== action.tagId);
      const newPresets = state.presets.map((p) => ({
        ...p,
        tags: p.tags.filter((tagId) => tagId !== action.tagId),
      }));
      return { ...state, tagDefinitions: newTagDefs, presets: newPresets };
    }

    case 'RENAME_TAG_DEFINITION': {
      const newTagDefs = state.tagDefinitions.map((t) =>
        t.id === action.tagId ? { ...t, name: action.name } : t,
      );
      return { ...state, tagDefinitions: newTagDefs };
    }

    case 'SET_REFERENCE_IMAGE': {
      const newConfigs = state.instanceConfigs.map((config, i) =>
        i === action.instanceId ? { ...config, referenceImage: action.referenceImage } : config,
      );
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'CLEAR_REFERENCE_IMAGE': {
      const newConfigs = state.instanceConfigs.map((config, i) =>
        i === action.instanceId ? { ...config, referenceImage: null } : config,
      );
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'ADD_MODE':
      return { ...state, modes: [...state.modes, action.mode] };

    case 'DELETE_MODE': {
      const modeToDelete = state.modes.find((m) => m.id === action.modeId);
      if (modeToDelete?.builtIn) return state;
      return { ...state, modes: state.modes.filter((m) => m.id !== action.modeId) };
    }

    case 'UPDATE_MODE': {
      const newModes = state.modes.map((m) => {
        if (m.id !== action.modeId) return m;
        // Built-in modes only allow tag updates
        if (m.builtIn) {
          if ('tags' in action.updates) {
            return { ...m, tags: action.updates.tags! };
          }
          return m;
        }
        return { ...m, ...action.updates };
      });
      return { ...state, modes: newModes };
    }

    case 'SET_BUILT_IN_MODES': {
      // Replace built-in modes while preserving user tag overrides and custom modes
      const userModes = state.modes.filter((m) => !m.builtIn);
      const existingTagOverrides: Record<string, string[]> = {};
      for (const m of state.modes) {
        if (m.builtIn && m.tags.length > 0) existingTagOverrides[m.id] = m.tags;
      }
      const resolvedBuiltIns = action.builtInModes.map((m) =>
        existingTagOverrides[m.id] ? { ...m, tags: existingTagOverrides[m.id] } : m,
      );
      return { ...state, modes: [...resolvedBuiltIns, ...userModes] };
    }

    default:
      return state;
  }
}

// ============================================================================
// Initial State Loader
// ============================================================================

/**
 * Builds the starting state from persisted records.
 *
 * Parsing lives in `../lib/image-generator-storage`; this function's own job is
 * the one invariant that spans both: every config must have a `modelSettings`
 * entry for its own `modelName`. A record can arrive without one (hand-edited
 * storage, or a catalogue swap between sessions), so each panel is run through
 * `resolveModelSettings` before it is handed to the reducer.
 */
function loadInitialState(userId: string): ImageGeneratorState {
  const storedConfigs = loadInstanceConfigs(userId);
  const instanceConfigs = storedConfigs.map((config) => {
    // No catalogue yet at mount, so no size repair here — `syncConfigsWithModels`
    // does that once the entity list lands.
    const settings = resolveModelSettings(config, config.modelName, storedConfigs);
    return config.modelSettings[config.modelName] === settings
      ? config
      : { ...config, modelSettings: { ...config.modelSettings, [config.modelName]: settings } };
  });

  const builtInTagOverrides = loadBuiltInModeTags(userId);
  const builtInModes = BUILT_IN_MODES.map((mode) =>
    builtInTagOverrides[mode.id] ? { ...mode, tags: builtInTagOverrides[mode.id] } : mode,
  );
  const layout = loadPanelLayout(userId);

  return {
    instanceConfigs,
    instanceStates: Array.from({ length: NUM_INSTANCES }, () => createDefaultInstanceState()),
    enabledInstances: layout.enabledInstances,
    minimizedInstances: layout.minimizedInstances,
    // URLs are presigned and expire; they are re-fetched once after mount.
    instanceImages: loadInstanceImages(userId),
    enhancementHistory: loadEnhancementHistory(userId),
    presets: loadPresets(userId),
    tagDefinitions: loadTagDefinitions(userId),
    modes: [...builtInModes, ...loadUserModes(userId)],
  };
}

// ============================================================================
// Hook Result Type
// ============================================================================

export interface UseImageGeneratorStateOptions {
  /** The authenticated user's ID, used to scope localStorage keys */
  userId: string;
  /** Callback invoked when preferences change (for session persistence) */
  onPreferencesChange?: (prefs: Partial<ImageGeneratorPreferences>) => void;
}

export interface UseImageGeneratorStateResult {
  state: ImageGeneratorState;
  // Config helpers
  /** Panel-level fields only — see `PanelConfigUpdates`. */
  updateInstanceConfig: (instanceId: number, updates: PanelConfigUpdates) => void;
  /** Switches a panel's model, restoring or seeding that model's settings. */
  setInstanceModel: (
    instanceId: number,
    modelName: string,
    options?: SetInstanceModelOptions,
  ) => void;
  /** Writes the panel's settings for its CURRENT model. */
  updateModelSettings: (
    instanceId: number,
    updates: Partial<ImageModelSettings>,
    models?: ModelSizeCapability[],
  ) => void;
  updateInstanceState: (instanceId: number, updates: Partial<ImageInstanceState>) => void;
  toggleInstance: (instanceId: number) => void;
  toggleMinimized: (instanceId: number) => void;
  canMinimize: (instanceId: number) => boolean;
  // Image helpers
  addImage: (instanceId: number, image: GeneratedImage) => void;
  removeImage: (instanceId: number, requestId: string) => void;
  clearAllImages: () => void;
  // Preset helpers
  addPreset: (preset: ImagePreset) => void;
  savePreset: (name: string, instanceId: number) => void;
  applyPresetToInstance: (
    instanceId: number,
    preset: ImagePreset | null,
    models?: ModelSizeCapability[],
  ) => void;
  overwritePreset: (presetId: string, instanceId: number) => void;
  deletePreset: (presetId: string) => void;
  updatePreset: (presetId: string, updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>) => void;
  // Tag helpers
  addTagDefinition: (name: string) => string;
  deleteTagDefinition: (tagId: string) => void;
  renameTagDefinition: (tagId: string, name: string) => void;
  // Mode helpers
  applyMode: (mode: ImageMode, models?: ModelSizeCapability[]) => void;
  saveMode: (name: string) => void;
  deleteMode: (modeId: string) => void;
  updateMode: (
    modeId: string,
    updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>,
  ) => void;
  // Reference image helpers
  setReferenceImage: (instanceId: number, referenceImage: ReferenceImage) => void;
  clearReferenceImage: (instanceId: number) => void;
  // Enhancement helpers
  updateEnhancement: (instanceId: number, key: EnhancementKey, value: string) => void;
  clearEnhancement: (instanceId: number, key: EnhancementKey) => void;
  selectFromHistory: (instanceId: number, key: EnhancementKey, value: string) => void;
  // Sync helper
  syncConfigsWithModels: (models: ModelDefinitionListItem[]) => void;
}

// ============================================================================
// Hook
// ============================================================================

/** Coalesces a burst of edits (an engine-param slider drag) into one write. */
const PERSIST_DEBOUNCE_MS = 300;

/**
 * Persists `value` under `userId` on a trailing debounce, flushing on unmount and
 * on `pagehide`.
 *
 * Used for the two records that are both large and hot: panel configs (an engine
 * param slider fires on every pointermove, and each config carries a settings
 * entry per model it has visited) and the saved-image gallery. The small records
 * — presets, tags, modes, layout, history — change rarely and write straight
 * through.
 *
 * Both flushes matter and neither subsumes the other: the unmount cleanup covers
 * navigating away mid-drag, and `pagehide` covers closing or reloading the tab,
 * where no React cleanup runs at all. Without them a debounce that keeps being
 * reset by a continuous drag would persist nothing for the whole drag.
 */
function useDebouncedPersist<T>(
  userId: string,
  value: T,
  save: (userId: string, value: T) => void,
): void {
  const latestRef = useRef(value);
  latestRef.current = value;
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    const handle = window.setTimeout(() => saveRef.current(userId, value), PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [userId, value]);

  useEffect(() => {
    const flush = () => saveRef.current(userId, latestRef.current);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [userId]);
}

export function useImageGeneratorState(
  options: UseImageGeneratorStateOptions,
): UseImageGeneratorStateResult {
  const { userId, onPreferencesChange } = options;
  const [state, dispatch] = useReducer(imageGeneratorReducer, userId, loadInitialState);

  // --- localStorage persistence effects ---

  useEffect(() => {
    saveEnhancementHistory(userId, state.enhancementHistory);
  }, [userId, state.enhancementHistory]);

  useEffect(() => {
    savePresets(userId, state.presets);
  }, [userId, state.presets]);

  useEffect(() => {
    saveTagDefinitions(userId, state.tagDefinitions);
  }, [userId, state.tagDefinitions]);

  useEffect(() => {
    saveUserModes(
      userId,
      state.modes.filter((m) => !m.builtIn),
    );
    const tagOverrides: Record<string, string[]> = {};
    for (const mode of state.modes) {
      if (mode.builtIn && mode.tags.length > 0) tagOverrides[mode.id] = mode.tags;
    }
    saveBuiltInModeTags(userId, tagOverrides);
  }, [userId, state.modes]);

  useDebouncedPersist(userId, state.instanceConfigs, saveInstanceConfigs);

  useEffect(() => {
    savePanelLayout(userId, {
      enabledInstances: state.enabledInstances,
      minimizedInstances: state.minimizedInstances,
    });
  }, [userId, state.enabledInstances, state.minimizedInstances]);

  useDebouncedPersist(userId, state.instanceImages, saveInstanceImages);

  // Refresh presigned URLs for restored images on mount
  const hasRefreshedUrls = useRef(false);
  useEffect(() => {
    if (hasRefreshedUrls.current) return;
    hasRefreshedUrls.current = true;

    const imagesToRefresh = state.instanceImages.flatMap((images, instanceId) =>
      images.filter((img) => img.imageRef && !img.imageUrl).map((img) => ({ instanceId, img })),
    );

    if (imagesToRefresh.length === 0) return;

    Promise.all(
      imagesToRefresh.map(async ({ instanceId, img }) => {
        try {
          const url = await api.getImageUrl(ARCHE_ID, img.imageRef);
          return { instanceId, requestId: img.requestId, url };
        } catch {
          return { instanceId, requestId: img.requestId, url: '' };
        }
      }),
    ).then((results) => {
      // Group by instance and dispatch SET_IMAGES with refreshed URLs
      for (let i = 0; i < NUM_INSTANCES; i++) {
        const updates = results.filter((r) => r.instanceId === i);
        if (updates.length === 0) continue;

        const urlMap = new Map(updates.map((u) => [u.requestId, u.url]));
        const refreshed = state.instanceImages[i].map((img) => ({
          ...img,
          imageUrl: urlMap.get(img.requestId) ?? img.imageUrl,
        }));
        // Remove images that failed to refresh (deleted from storage)
        const valid = refreshed.filter((img) => img.imageUrl !== '');
        dispatch({ type: 'SET_IMAGES', instanceId: i, images: valid });
      }
    });
  }, []);

  // --- Session persistence: sync first instance config to server ---
  // Skip the initial render to avoid saving defaults on mount (matches SingleAI/MultiAI pattern)
  const firstConfig = state.instanceConfigs[0];
  const firstSettings = getActiveModelSettings(firstConfig);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (onPreferencesChange) {
      onPreferencesChange({
        selectedProvider: firstConfig.provider as ImageGeneratorPreferences['selectedProvider'],
        selectedModelId: firstConfig.modelName,
        imageSettings: {
          size: firstSettings.size,
          quality: firstSettings.quality,
          style: firstSettings.style,
        },
      });
    }
    // Primitives only. Putting the settings OBJECT here would re-fire whenever
    // `getActiveModelSettings` returned a new reference and loop on the server.
  }, [
    firstConfig.provider,
    firstConfig.modelName,
    firstSettings.size,
    firstSettings.quality,
    firstSettings.style,
    onPreferencesChange,
  ]);

  // --- Dispatch-wrapping callbacks ---

  const updateInstanceConfig = useCallback((instanceId: number, updates: PanelConfigUpdates) => {
    dispatch({ type: 'UPDATE_CONFIG', instanceId, updates });
  }, []);

  // `at` is generated here rather than inside the reducer so the reducer stays a
  // pure function of (state, action) and its tests stay deterministic.
  const setInstanceModel = useCallback(
    (instanceId: number, modelName: string, options: SetInstanceModelOptions = {}) => {
      dispatch({
        type: 'SET_MODEL',
        instanceId,
        modelName,
        provider: options.provider,
        models: options.models,
        settings: options.settings,
        at: Date.now(),
      });
    },
    [],
  );

  const updateModelSettings = useCallback(
    (instanceId: number, updates: Partial<ImageModelSettings>, models?: ModelSizeCapability[]) => {
      dispatch({ type: 'UPDATE_MODEL_SETTINGS', instanceId, updates, models, at: Date.now() });
    },
    [],
  );

  const updateInstanceState = useCallback(
    (instanceId: number, updates: Partial<ImageInstanceState>) => {
      dispatch({ type: 'UPDATE_STATE', instanceId, updates });
    },
    [],
  );

  const toggleInstance = useCallback((instanceId: number) => {
    dispatch({ type: 'TOGGLE_INSTANCE', instanceId });
  }, []);

  const toggleMinimized = useCallback((instanceId: number) => {
    dispatch({ type: 'TOGGLE_MINIMIZED', instanceId });
  }, []);

  const canMinimize = useCallback(
    (instanceId: number) => {
      if (state.minimizedInstances[instanceId]) return true;
      const enabledAndExpanded = state.enabledInstances.filter(
        (enabled, i) => enabled && !state.minimizedInstances[i],
      );
      return enabledAndExpanded.length > 1;
    },
    [state.enabledInstances, state.minimizedInstances],
  );

  const addImage = useCallback((instanceId: number, image: GeneratedImage) => {
    dispatch({ type: 'ADD_IMAGE', instanceId, image });
  }, []);

  const clearAllImages = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_IMAGES' });
  }, []);

  const removeImage = useCallback((instanceId: number, requestId: string) => {
    dispatch({ type: 'REMOVE_IMAGE', instanceId, requestId });
  }, []);

  const addPreset = useCallback((preset: ImagePreset) => {
    dispatch({ type: 'ADD_PRESET', preset });
  }, []);

  const savePreset = useCallback(
    (name: string, instanceId: number) => {
      const config = state.instanceConfigs[instanceId];
      const settings = getActiveModelSettings(config);
      const preset: ImagePreset = {
        id: `preset_${crypto.randomUUID()}`,
        name,
        size: settings.size,
        quality: settings.quality,
        style: settings.style,
        enhancements: { ...config.enhancements },
        providerParams: { ...settings.providerParams },
        ...(config.includeModelInPreset
          ? { modelName: config.modelName, provider: config.provider }
          : {}),
        createdAt: new Date().toISOString(),
        tags: [],
        categoryId: null,
      };
      dispatch({ type: 'ADD_PRESET', preset });
      // `originalPresetId` MUST move with the name. `reconcilePresetName` resolves
      // it before falling back to a name match, so leaving it pointing at the
      // previously-loaded preset makes the next edit rename the panel back to
      // that preset — and aims the "Update …" button at the wrong one.
      dispatch({
        type: 'UPDATE_CONFIG',
        instanceId,
        updates: { presetName: name, originalPresetId: preset.id },
      });
    },
    [state.instanceConfigs],
  );

  /**
   * Applies a preset to one panel, or resets the panel when `preset` is null.
   *
   * Either way only the panel's entry for the model it ends up on is written —
   * every other model it remembers survives, so switching away and back still
   * restores. That is the deliberate rule: a preset is an explicit user act on
   * the current model, not a wipe of the panel's memory.
   */
  const applyPresetToInstance = useCallback(
    (instanceId: number, preset: ImagePreset | null, models?: ModelSizeCapability[]) => {
      const at = Date.now();
      if (preset === null) {
        dispatch({
          type: 'UPDATE_MODEL_SETTINGS',
          instanceId,
          updates: createDefaultModelSettings(at),
          models,
          at,
          panel: {
            presetName: 'Default Preset',
            originalPresetId: null,
            includeModelInPreset: false,
            enhancements: { ...EMPTY_ENHANCEMENTS },
            referenceImage: null,
          },
        });
        return;
      }

      const panel: PanelConfigUpdates = {
        presetName: preset.name,
        originalPresetId: preset.id,
        includeModelInPreset: !!preset.modelName,
        enhancements: { ...preset.enhancements },
        referenceImage: null,
      };
      const settings: Partial<ImageModelSettings> = {
        size: preset.size,
        quality: preset.quality,
        style: preset.style,
        providerParams: { ...(preset.providerParams || {}) },
      };

      if (preset.modelName) {
        dispatch({
          type: 'SET_MODEL',
          instanceId,
          modelName: preset.modelName,
          provider: preset.provider,
          models,
          settings,
          panel,
          at,
        });
        return;
      }
      dispatch({ type: 'UPDATE_MODEL_SETTINGS', instanceId, updates: settings, models, at, panel });
    },
    [],
  );

  const overwritePreset = useCallback((presetId: string, instanceId: number) => {
    dispatch({ type: 'OVERWRITE_PRESET', presetId, instanceId });
  }, []);

  const deletePreset = useCallback((presetId: string) => {
    dispatch({ type: 'DELETE_PRESET', presetId });
  }, []);

  const updatePreset = useCallback(
    (presetId: string, updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>) => {
      dispatch({ type: 'UPDATE_PRESET', presetId, updates });
    },
    [],
  );

  const addTagDefinition = useCallback((name: string): string => {
    const tag: TagDefinition = { id: `tag_${crypto.randomUUID()}`, name };
    dispatch({ type: 'ADD_TAG_DEFINITION', tag });
    return tag.id;
  }, []);

  const deleteTagDefinition = useCallback((tagId: string) => {
    dispatch({ type: 'DELETE_TAG_DEFINITION', tagId });
  }, []);

  const renameTagDefinition = useCallback((tagId: string, name: string) => {
    dispatch({ type: 'RENAME_TAG_DEFINITION', tagId, name });
  }, []);

  const applyMode = useCallback(
    (mode: ImageMode, models?: ModelSizeCapability[]) => {
      const at = Date.now();
      mode.panelConfigs.forEach((panelConfig, i) => {
        // One atomic action per panel: the mode's settings must land in the
        // entry for the mode's model, not in whatever the panel was showing.
        dispatch({
          type: 'SET_MODEL',
          instanceId: i,
          modelName: panelConfig.modelName,
          provider: panelConfig.provider,
          models,
          at,
          settings: {
            size: panelConfig.size,
            quality: panelConfig.quality,
            style: panelConfig.style,
            providerParams: { ...panelConfig.providerParams },
          },
          panel: {
            presetName: mode.name,
            originalPresetId: null,
            enhancements: { ...panelConfig.enhancements },
            referenceImage: null,
          },
        });
      });
      // Un-minimize all panels
      for (let i = 0; i < NUM_INSTANCES; i++) {
        if (state.minimizedInstances[i]) {
          dispatch({ type: 'TOGGLE_MINIMIZED', instanceId: i });
        }
      }
    },
    [state.minimizedInstances],
  );

  const saveMode = useCallback(
    (name: string) => {
      const panelConfigs = state.instanceConfigs.map((config) => {
        const settings = getActiveModelSettings(config);
        return {
          modelName: config.modelName,
          provider: config.provider,
          size: settings.size,
          quality: settings.quality,
          style: settings.style,
          enhancements: { ...config.enhancements },
          providerParams: { ...settings.providerParams },
        };
      });

      const mode: ImageMode = {
        id: `mode_${crypto.randomUUID()}`,
        name,
        description: '',
        builtIn: false,
        panelConfigs,
        tags: [],
        createdAt: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_MODE', mode });
    },
    [state.instanceConfigs],
  );

  const deleteMode = useCallback((modeId: string) => {
    dispatch({ type: 'DELETE_MODE', modeId });
  }, []);

  const updateMode = useCallback(
    (modeId: string, updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>) => {
      dispatch({ type: 'UPDATE_MODE', modeId, updates });
    },
    [],
  );

  const setReferenceImage = useCallback((instanceId: number, referenceImage: ReferenceImage) => {
    dispatch({ type: 'SET_REFERENCE_IMAGE', instanceId, referenceImage });
  }, []);

  const clearReferenceImage = useCallback((instanceId: number) => {
    dispatch({ type: 'CLEAR_REFERENCE_IMAGE', instanceId });
  }, []);

  const updateEnhancement = useCallback(
    (instanceId: number, key: EnhancementKey, value: string) => {
      dispatch({ type: 'UPDATE_ENHANCEMENT', instanceId, key, value });
    },
    [],
  );

  const clearEnhancement = useCallback(
    (instanceId: number, key: EnhancementKey) => {
      const currentValue = state.instanceConfigs[instanceId]?.enhancements[key]?.trim();
      if (currentValue) {
        dispatch({ type: 'ADD_ENHANCEMENT_HISTORY', key, value: currentValue });
      }
      dispatch({ type: 'CLEAR_ENHANCEMENT', instanceId, key });
    },
    [state.instanceConfigs],
  );

  const selectFromHistory = useCallback(
    (instanceId: number, key: EnhancementKey, value: string) => {
      dispatch({ type: 'UPDATE_ENHANCEMENT', instanceId, key, value });
    },
    [],
  );

  /**
   * Reconciles panels against the entity catalogue once it loads.
   *
   * Two repairs, both scoped to the ACTIVE model: swap a model that is no longer
   * in the catalogue, and fix a size the active model cannot accept. A panel
   * restored from storage is active on a model without ever passing through
   * `SET_MODEL`, and the catalogue only arrives after mount, so this is the one
   * activation path the reducer does not own.
   *
   * Entries for OTHER models are deliberately left alone. `availableModels` can
   * be transiently stale or short, and rewriting a stashed size on that basis
   * would destroy a setting the user never touched — while a stashed invalid size
   * is inert, since size only reaches the API from the active entry. A model that
   * has dropped out of the catalogue likewise keeps its entry: if it comes back,
   * so do its settings.
   */
  const syncConfigsWithModels = useCallback(
    (models: ModelDefinitionListItem[]) => {
      if (models.length === 0) return;
      // Resolve dynamic built-in modes (e.g. Provider Shootout) from entities
      dispatch({ type: 'SET_BUILT_IN_MODES', builtInModes: resolveBuiltInModes(models) });

      const newConfigs = state.instanceConfigs.map((config) => {
        const isValidSelection = models.some((m) => m.modelName === config.modelName);
        const modelName = isValidSelection ? config.modelName : models[0].modelName;
        const provider = isValidSelection ? config.provider : models[0].provider;
        // Route through the resolver even when the model is unchanged: it is what
        // guarantees the destination model HAS an entry. Patching `modelName`
        // directly would leave the active model pointing at nothing.
        const settings = resolveModelSettings(config, modelName, state.instanceConfigs, models);
        if (
          modelName === config.modelName &&
          provider === config.provider &&
          config.modelSettings[modelName] === settings
        ) {
          return config;
        }
        return {
          ...config,
          modelName,
          provider,
          modelSettings: { ...config.modelSettings, [modelName]: settings },
        };
      });
      if (newConfigs.some((c, i) => c !== state.instanceConfigs[i])) {
        dispatch({ type: 'SET_CONFIGS', configs: newConfigs });
      }
    },
    [state.instanceConfigs],
  );

  return useMemo(
    () => ({
      state,
      updateInstanceConfig,
      setInstanceModel,
      updateModelSettings,
      updateInstanceState,
      toggleInstance,
      toggleMinimized,
      canMinimize,
      addImage,
      removeImage,
      clearAllImages,
      addPreset,
      savePreset,
      applyPresetToInstance,
      overwritePreset,
      deletePreset,
      updatePreset,
      addTagDefinition,
      deleteTagDefinition,
      renameTagDefinition,
      setReferenceImage,
      clearReferenceImage,
      applyMode,
      saveMode,
      deleteMode,
      updateMode,
      updateEnhancement,
      clearEnhancement,
      selectFromHistory,
      syncConfigsWithModels,
    }),
    [
      state,
      updateInstanceConfig,
      setInstanceModel,
      updateModelSettings,
      updateInstanceState,
      toggleInstance,
      toggleMinimized,
      canMinimize,
      addImage,
      removeImage,
      clearAllImages,
      addPreset,
      savePreset,
      applyPresetToInstance,
      overwritePreset,
      deletePreset,
      updatePreset,
      addTagDefinition,
      deleteTagDefinition,
      renameTagDefinition,
      setReferenceImage,
      clearReferenceImage,
      applyMode,
      saveMode,
      deleteMode,
      updateMode,
      updateEnhancement,
      clearEnhancement,
      selectFromHistory,
      syncConfigsWithModels,
    ],
  );
}
